import ServiceWrapper from 'Services/ServiceWrapper';
import AppSettings from 'Settings/AppSettings';
import AuthManager from 'Common/AuthManager';
import { createHttpProductCardsRepository } from 'Pages/GMS/ProductCards/services';
import { ApplicationRef } from 'Pages/GMS/ProductCards/types';
import { ManifestSection, ResolveConfigOverrides } from '../manifest/types';

export interface OptionValue {
  value: string | number;
  label: string;
}

const BASE_URL = AppSettings.GMS_BACKEND_API_URL;

/**
 * Thrown when the backend's `@check_rbac('Resolve', 'configure')` denies the request.
 * `check_rbac` returns `{done: false, message: "Permission denied!!"}` with an HTTP 200 —
 * NOT a 401/403 — so `response.errorfound` (transport/HTTP-level failure) is false for a
 * denial; the refusal only shows up in the body. Callers should catch this specifically to
 * show a "you don't have permission" state instead of the generic load-failed one (the same
 * distinction `Pages/GMS/ComputedSubstitutions/.../IterationTable.tsx` and
 * `Components/Admin/Logs/LogsList.jsx` already make against this exact backend behaviour).
 */
export class ResolveConfigForbiddenError extends Error {}

/**
 * Resolve config is scoped by Environment (`channelKey`), the same scope key ISR's
 * merchant config uses (`isr/merchant/config/${channelKey}`). There is no admin-only
 * settings tier (removed 2026-09-24) — this service passes the token through and trusts
 * the response as-is.
 *
 * The backend response is `{ done: true, data: { sections: [...] } }` — every visible
 * manifest setting nested by section, each carrying its own schema (title, description,
 * control, options, depends_on, children) AND its resolved `value`. Unlike the previous
 * version of this function, the schema is kept — the generic renderer
 * (`ResolveGenericSection`/`ResolveControlRenderer`) needs it to know what to render, not
 * just what value to show. See `Docs/api/resolve/resolve.md` §4 in the backend repo for the
 * full shape.
 */
export const getResolveConfig = async (channelKey: string): Promise<ManifestSection[]> => {
  const response = await ServiceWrapper.doGet(
    BASE_URL + `resolve/config/${channelKey}`,
    null,
    null,
    AuthManager.getGmsToken(),
  );
  if (response?.errorfound) throw new Error('Failed to load Resolve configuration.');
  if (response?.data?.done === false) {
    throw new ResolveConfigForbiddenError(response.data.message || 'Permission denied!!');
  }
  return response?.data?.data?.sections ?? [];
};

/** Flattens the loaded `sections[]` (each setting carrying its own resolved `value`) down
 * to the flat `{name: value}` map `ResolveConfigContext` seeds its live edit state from —
 * the shape the PUT payload and `ResolveProductCardButton` (via `overrides.product_card_id`)
 * still work with. */
export const flattenSectionsToValues = (sections: ManifestSection[]): ResolveConfigOverrides => {
  const values: ResolveConfigOverrides = {};
  for (const section of sections) {
    for (const setting of section.settings ?? []) {
      values[setting.name] = setting.value;
    }
  }
  return values;
};

export interface ProductCardOptionsPage {
  rows: OptionValue[];
  total: number;
}

const PRODUCT_CARD_OPTIONS_LIMIT = 20;

/**
 * Resolves the manifest's `options.source: "gms.product_cards"` — the one dropdown
 * whose choices come from data instead of static `options.values`. Only the listing
 * fields this dropdown needs (`id`/`name`) are read; `partyId`/`industryId` are
 * create-only on that repository and unused by `list()`.
 *
 * Paginated (`PRODUCT_CARD_OPTIONS_LIMIT` rows/page) and search-driven rather than a
 * one-shot fetch of everything — a channel can hold far more than one page's worth of
 * cards, and `ResolveProductCardButton` drives paging via the Dropdown's `onScrollEnd`.
 */
export const getProductCardOptionsPage = async (
  channelKey: string,
  { page, search }: { page: number; search: string },
): Promise<ProductCardOptionsPage> => {
  const repository = createHttpProductCardsRepository({ csKey: channelKey, partyId: '', industryId: '' });
  const { rows, total } = await repository.list({
    page,
    limit: PRODUCT_CARD_OPTIONS_LIMIT,
    search,
    sortBy: 'name',
    sortDir: 'asc',
  });
  return { rows: rows.map((row) => ({ value: row.id, label: row.name })), total };
};

/**
 * Resolves one card's current `{value, label}` by id, independent of paging/search —
 * `product_card_id` is stored as a bare id with no name of its own (see
 * `Docs/api/resolve/resolve.md` §8 in the backend repo), so a saved selection outside
 * whatever page `getProductCardOptionsPage` happens to have loaded would otherwise have
 * no label to show. `null` when the card no longer exists (`repository.get` returns
 * `null` for "not found" rather than throwing) — callers fall back to the Dropdown's own
 * placeholder in that case, same as if nothing were selected.
 */
export const getProductCardById = async (channelKey: string, cardId: string): Promise<OptionValue | null> => {
  const repository = createHttpProductCardsRepository({ csKey: channelKey, partyId: '', industryId: '' });
  const card = await repository.get(cardId);
  return card ? { value: card.id, label: card.name } : null;
};

const RESOLVE_APPLICATION_REF: ApplicationRef = 'resolve';

/**
 * Keeps a card's `references` authoring tag in sync with Resolve's own selection.
 * This is an editor-facing hint, not the enforcement mechanism: the backend's "in
 * use" lock reads Resolve's OWN stored config, never this field (see
 * `Docs/api/product-cards/product-cards.md` §10.3/§10.9 in the backend repo — the
 * lock is a pull, deliberately not this write). Best-effort: a card that no longer
 * exists, or a write that fails (e.g. a stale revision from someone editing the
 * card in the Product Cards screen at the same time), is swallowed rather than
 * blocking the Resolve config save that triggered it.
 */
const setCardReferencesResolveTag = async (
  channelKey: string,
  cardId: string,
  referenced: boolean,
): Promise<void> => {
  const repository = createHttpProductCardsRepository({ csKey: channelKey, partyId: '', industryId: '' });
  try {
    const card = await repository.get(cardId);
    if (!card) return;
    const hasTag = card.references.includes(RESOLVE_APPLICATION_REF);
    if (hasTag === referenced) return;
    const nextReferences = referenced
      ? [...card.references, RESOLVE_APPLICATION_REF]
      : card.references.filter((ref) => ref !== RESOLVE_APPLICATION_REF);
    await repository.save({ ...card, references: nextReferences });
  } catch {
    // Best-effort — see docblock above.
  }
};

/**
 * Called after a Resolve config save in which `product_card_id` changed. Untags
 * the previously-selected card and tags the newly-selected one; either side is
 * skipped when there is no card on that side (a fresh selection or a clear).
 */
export const syncResolveCardReference = async (
  channelKey: string,
  previousCardId: string | null,
  nextCardId: string | null,
): Promise<void> => {
  if (previousCardId === nextCardId) return;
  await Promise.all([
    previousCardId ? setCardReferencesResolveTag(channelKey, previousCardId, false) : Promise.resolve(),
    nextCardId ? setCardReferencesResolveTag(channelKey, nextCardId, true) : Promise.resolve(),
  ]);
};

export const updateResolveConfig = async (
  channelKey: string,
  overrides: ResolveConfigOverrides,
): Promise<boolean> => {
  const response = await ServiceWrapper.doPut(
    BASE_URL + `resolve/config/${channelKey}`,
    { overrides },
    null,
    AuthManager.getGmsToken(),
  );
  // `doPut` uses fetch, which does not throw on a 400/500, and an RBAC denial is a 200 with
  // `done: false`, so only `done: true` from the backend means the save landed.
  return Boolean(response && !response.errorfound && response.data?.done === true);
};
